// eslint-disable-next-line @typescript-eslint/ban-ts-comment
import { LoadingState } from '@/_models/virtualStaging/VSObject';
import { createHTMLMesh } from '@/_models/virtualStaging/HTMLMesh/HTMLMesh';
import { InteractiveGroup } from '@/_models/virtualStaging/HTMLMesh/utils/InteractiveGroup';
import { getXZCoordinatesOnCircle } from '@/components/util/math/getXZCoordinatesOnCircle';
import { AbstractHTMLMesh } from '@/_models/virtualStaging/HTMLMesh/component/AbstractHTMLMesh';
import { ComponentInteractionType } from '@/_models/virtualStaging/Scene.component';
import { getRelativePaddingTop } from '@/_models/virtualStaging/HTMLMesh/utils/getRelativePaddingTop';
import { createCircleIconSVG } from '@/_models/virtualStaging/HTMLMesh/utils/createSVG';
import { svgToPng } from '../../utils/svgToPng';
import { Inputs } from '@/_models/virtualStaging/HTMLMesh/component/MatterTag/types';

export class MatterTagMeshComponent extends AbstractHTMLMesh {
  public tagId;

  private hostPose;

  public popupMesh;

  public popupMeshInteractiveGroup;

  public mediaMesh;

  public refs: Record<string, unknown>;

  public tag;

  public textureLoader;

  inputs: Inputs = {
    html: null,
    tagData: null,
    media: null,
    dimensions: { width: 400, height: 400 },
    scale: 1,
    visible: true,
    popupVisible: false,
    popupScale: 16,
  };

  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false,
  };

  onInit() {
    const THREE = this.context.three;
    const { html, media } = this.inputs;
    this.tagId = this.inputs.tagData.id;
    this.initRoot();
    this.textureLoader = new THREE.TextureLoader();
    this.initTag();

    this.initPopupMesh(html);
    if (media) this.initMedia(media);
    this.addMeshPointerEvents();
    this.getXRController();

    this.outputs.loadingState = LoadingState.Loaded;
  }

  async initTag() {
    const THREE = this.context.three;
    let iconSVG;
    const tag = this.inputs.tagData;
    let tagIconUrl;
    if (tag.iconFile) {
      if (tag.iconFile.startsWith('http')) {
        tagIconUrl = `${tag.iconFile}`;
      } else {
        tagIconUrl = `${process.env.NEXT_PUBLIC_CDN}/tours/${tag.tourId}/tags/icons/${tag.iconFile}`;
      }
    } else if (tag.customIcon && tag.customIcon !== 'null') {
      tagIconUrl = `${process.env.NEXT_PUBLIC_API}/v1/public/icons/${tag.customIcon}?hex=${encodeURIComponent(tag.iconColor)}&circle=${
        tag.whiteCircle
      }`;
    }

    if (this.inputs.tagData.iconColor) iconSVG = createCircleIconSVG(this.inputs.tagData.iconColor);
    else if (!this.inputs.tagData.iconColor) iconSVG = createCircleIconSVG('#03687d');

    const iconSrc = tagIconUrl || svgToPng(iconSVG);

    this.textureLoader.load(iconSrc, (texture) => {
      texture.image.width = 128;
      texture.image.height = 128;
      const aspect = texture.image.width / texture.image.height;
      const [width, height] = aspect >= 1 ? [1, 1 / aspect] : [1 / aspect, 1];

      const geometry = new THREE.PlaneGeometry(width, height);
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.DoubleSide,
        blending: THREE.NormalBlending,
        transparent: true,
      });
      this.tag = new THREE.Mesh(geometry, material);
      this.tag.name = 'tag';
      this.pivot.add(this.tag);
      this.root.position.set(
        this.inputs.tagData.positionX + this.inputs.tagData.stemVectorX,
        this.inputs.tagData.positionY + this.inputs.tagData.stemVectorY,
        this.inputs.tagData.positionZ + this.inputs.tagData.stemVectorZ,
      );
    });
  }

  initPopupMesh(html) {
    this.popupMesh = createHTMLMesh(html, this.context.three);
    this.popupMesh.material.side = this.context.three.DoubleSide;
    this.popupMesh.material.depthWrite = false;
    this.popupMesh.material.depthTest = false;
    this.popupMesh.renderOrder = 970;
    this.popupMesh.scale.setScalar(0);
    this.popupMesh.userData = { isPointerOver: false };
  }

  initMedia(media) {
    const THREE = this.context.three;
    this.textureLoader.load(media, (texture) => {
      if (!texture) return;
      const texWidth = texture.image.width;
      const texHeight = texture.image.height;

      // Calculate the aspect ratio of the texture
      const aspectRatio = texWidth / texHeight;

      // Set the width and height of the mesh
      const meshWidth = 0.35;
      const meshHeight = 0.35 / aspectRatio;

      // Create the plane geometry and mesh
      const geometry = new THREE.PlaneGeometry(meshWidth, meshHeight);
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.DoubleSide,
        blending: THREE.NormalBlending,
        transparent: true,
      });

      this.mediaMesh = new THREE.Mesh(geometry, material);
      this.mediaMesh.material.depthWrite = false;
      this.mediaMesh.material.depthTest = false;
      this.mediaMesh.renderOrder = 975;
      this.mediaMesh.position.z = 0.01;

      const offset = getRelativePaddingTop(this.inputs.html);
      this.mediaMesh.position.y =
        (this.popupMesh.geometry.parameters.height - this.mediaMesh.geometry.parameters.height) *
          0.5 -
        offset * this.popupMesh.geometry.parameters.height;
      this.popupMesh.add(this.mediaMesh);
    });
  }

  initCloseBtn() {
    const THREE = this.context.three;
    const geometry = new THREE.PlaneGeometry(1, 1);
    const texture = new THREE.TextureLoader().load('/images/close-icon.png');
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.DoubleSide,
      transparent: true,
      color: 'white',
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(0.25, this.popupMesh.geometry.parameters.height * 0.5, 0);
    mesh.scale.setScalar(0.1);
    mesh.name = 'closeBtn';
    mesh.renderOrder = 980;
    this.popupMesh.add(mesh);
    mesh.position.z = -0.1;
  }

  addMeshPointerEvents() {
    const group = new InteractiveGroup(this.context.renderer, this.context.camera, this.context);

    this.context.camera.parent.parent.add(group);
    this.root.add(group);
    this.popupMeshInteractiveGroup = group;
    group.add(this.popupMesh);

    this.initCloseBtn();
  }

  setHostPose(pose) {
    if (!pose) return;
    this.hostPose = pose;
  }

  setPopupFromPose(pose) {
    const THREE = this.context.three;
    const { x, z } = getXZCoordinatesOnCircle(0.85, pose.rotation.y + 180);
    const worldPosition = new THREE.Vector3(
      pose.position.x + x,
      pose.position.y - 0.2,
      pose.position.z + z,
    ); // Replace with your world coordinates
    const localPosition = this.root.worldToLocal(worldPosition);

    this.popupMeshInteractiveGroup.position.copy(localPosition);
    this.popupMeshInteractiveGroup.lookAt(pose.position.x, pose.position.y - 0.1, pose.position.z);
  }

  setPopupFromHostPose() {
    this.setPopupFromPose(this.hostPose);
  }

  cycleVR(session) {
    this.lookAtHost();
  }

  lookAtHost() {
    this.root.lookAt(this.hostPose.position.x, this.hostPose.position.y, this.hostPose.position.z);
    this.popupMesh.lookAt(
      this.hostPose.position.x,
      this.hostPose.position.y,
      this.hostPose.position.z,
    );
  }

  onInputsUpdated(previousInputs: this['inputs']) {
    if (previousInputs.popupVisible !== this.inputs.popupVisible) {
      if (this.inputs.popupVisible) {
        this.popupMesh.scale.setScalar(this.inputs.popupScale);
        if (this.hostPose) this.setPopupFromHostPose();
      } else this.popupMesh.scale.setScalar(0);
    }
    if (previousInputs.visible !== this.inputs.visible) {
      this.root.visible = this.inputs.visible;
    }
  }

  onTick() {
    if (this.context?.renderer?.xr?.isPresenting) {
      const _session = this.context.renderer.xr.getSession();
      _session.requestAnimationFrame(() => this.cycleVR(_session));
    }
  }
}

export const matterTagMeshComponentType = 'mp.matterTagMeshComponent';
export const makeMatterTagMeshComponent = (): any => new MatterTagMeshComponent();
